import type {ComposerAttachment, ChatComposerProps} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {useMemo} from 'react'

import {useReferenceActivation, type ReferenceActivation} from '../contexts/ReferenceActivationContext'
import {messageAttachmentUrl, normalizeReferenceActivation} from '../references/reference-activation'
import {implicitReferenceToAttachment} from '../references/implicit-context'
import type {ComposerAttachmentsController} from './use-composer-attachments'

export interface ComposerActivationTarget {
  id: string
  target: ReferenceActivation
}

const EMPTY_TARGETS: readonly ComposerActivationTarget[] = []
const EMPTY_ATTACHMENTS: ComposerAttachment[] = []

/** Resolve presentation IDs against typed attachments, never against labels or icons. */
export function useComposerReferenceActivation(
  controller: ComposerAttachmentsController,
  attachments: ComposerAttachment[] = EMPTY_ATTACHMENTS,
  additionalTargets: readonly ComposerActivationTarget[] = EMPTY_TARGETS,
): Pick<ChatComposerProps, 'attachments' | 'onAttachmentClick'> {
  const activate = useReferenceActivation()
  return useMemo(() => {
    const targets = new Map<string, ReferenceActivation>()
    for (const item of [...controller.tracked, ...controller.pastedReferences]) {
      targets.set(item.token.id, {kind: 'attachment', attachment: item.ahp, id: item.token.id})
    }
    for (const repo of controller.selectedRepos) {
      const [ownerLogin, name] = repo.name.split('/')
      if (!ownerLogin || !name) continue
      const attachment = implicitReferenceToAttachment({type: 'repository', id: repo.id, ownerLogin, name})
      if (attachment) targets.set(`repo-attachment:${repo.id}`, {kind: 'attachment', attachment})
    }
    for (const {id, target} of additionalTargets) targets.set(id, target)
    return {
      attachments: attachments.map(attachment => {
        if (attachment.url) return attachment
        const target = targets.get(attachment.id)
        if (!target) return attachment
        const normalized = normalizeReferenceActivation(target)
        const url =
          target.kind === 'attachment'
            ? messageAttachmentUrl(target.attachment)
            : normalized?.kind === 'file'
              ? normalized.reference.url
              : normalized?.kind === 'repository' || normalized?.kind === 'space'
                ? normalized.url
                : undefined
        return url ? {...attachment, url} : attachment
      }),
      onAttachmentClick: activate
        ? (attachment, event) => {
            const target = targets.get(attachment.id)
            if (target) activate(target, event)
          }
        : undefined,
    }
  }, [
    activate,
    additionalTargets,
    attachments,
    controller.pastedReferences,
    controller.selectedRepos,
    controller.tracked,
  ])
}
